// src/Redux/slices/followsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  followDoctorThunk,
  unfollowDoctorThunk,
  getFollowedDoctorsThunk,
} from "../thunks/followsThunks";

const toStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

const pickFolloweeId = (item) => {
  const id = toStr(item?.followeeId || item?.doctorId || item?.followee_id || item?.doctor_id);
  return id;
};

const pickFolloweeType = (item) =>
  item?.followeeType === "user" || item?.followeeType === "patient"
    ? "patient"
    : "doctor";

const upsertByFolloweeId = (list, item) => {
  const id = pickFolloweeId(item);
  if (!id) return list;

  const next = Array.isArray(list) ? [...list] : [];
  const type = pickFolloweeType(item);
  const idx = next.findIndex(
    (x) => pickFolloweeId(x) === id && pickFolloweeType(x) === type,
  );

  const normalized = {
    ...item,
    followeeId: id,
    followeeType: type,
    ...(type === "doctor" ? { doctorId: id } : {}),
  };

  if (idx >= 0) next[idx] = { ...next[idx], ...normalized };
  else next.unshift(normalized);

  return next;
};

const removeByFolloweeId = (list, followeeId, followeeType) => {
  const id = toStr(followeeId);
  if (!id) return Array.isArray(list) ? list : [];
  const type = pickFolloweeType({ followeeType });
  return (Array.isArray(list) ? list : []).filter(
    (x) => pickFolloweeId(x) !== id || pickFolloweeType(x) !== type,
  );
};

const initialState = {
  followedDoctors: [],
  loading: false,
  loaded: false, // IMPORTANT: sert à statusReady
  error: null,
};

const followsSlice = createSlice({
  name: "follows",
  initialState,
  reducers: {
    markFollowsLoaded(state) {
      state.loaded = true;
    },
    clearFollowsError(state) {
      state.error = null;
    },
    resetFollows(state) {
      state.followedDoctors = [];
      state.loading = false;
      state.loaded = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GET FOLLOWED =====
      .addCase(getFollowedDoctorsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFollowedDoctorsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅
        state.followedDoctors = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getFollowedDoctorsThunk.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅ CRUCIAL sinon UI bloquée
        state.error = action.payload || action.error?.message || "Failed to load follows";
      })

      // ===== FOLLOW =====
      .addCase(followDoctorThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(followDoctorThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅
        state.followedDoctors = upsertByFolloweeId(state.followedDoctors, action.payload || {});
      })
      .addCase(followDoctorThunk.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅ évite blocage
        state.error = action.payload || action.error?.message || "Follow failed";
      })

      // ===== UNFOLLOW =====
      .addCase(unfollowDoctorThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unfollowDoctorThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅
        state.followedDoctors = removeByFolloweeId(
          state.followedDoctors,
          action.payload?.followeeId,
          action.payload?.followeeType,
        );
      })
      .addCase(unfollowDoctorThunk.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true; // ✅ évite blocage
        state.error = action.payload || action.error?.message || "Unfollow failed";
      });
  },
});

export const { markFollowsLoaded, clearFollowsError, resetFollows } = followsSlice.actions;
export default followsSlice.reducer;